import { Space }                                  from 'antd';
import { FormInstance }                           from 'antd';
import { withNavigation }                         from 'components/App/withNavigation';
import { WithNavigationProps }                    from 'components/App/withNavigation';
import React                                      from 'react';
import { Form }                                   from 'antd';
import { Button }                                 from 'antd';
import { message }                                from 'antd';
import { NavLink }                                from 'react-router-dom';
import { Link }                                   from 'react-router-dom';
import { UserOutlined }                           from '@ant-design/icons';
import { LockOutlined }                           from '@ant-design/icons';
import { observer }                               from 'mobx-react';
import { jwtDecode }                              from 'jwt-decode';
import Input                                      from 'components/App/IntlInput';
import { privateLayoutStore }                     from 'stores/index';
import currentFilters                             from 'utils/currentFilters';
import currentSession                             from 'utils/currentSession';
import ExternalLoginCheckModel                    from 'models/ExternalLoginCheckModel';
import FormattedMessageWrapper                    from 'components/App/FormattedMessageWrapper';
import { ApiCollection }                          from 'collections/ApiCollection';
import ExternalStandIdentifierModel               from 'models/ExternalStandIdentifierModel';
import { injectIntl, type WrappedComponentProps } from 'react-intl';
import { messageErrorIntl }                       from 'utils/messageIntl';

const findGetParameter = (parameterName: string): string | null => {
  let result = null;
  let tmp = [];
  window.location.search
    .substr(1)
    .split('&')
    .forEach(item => {
      tmp = item.split('=');
      if (tmp[0] === parameterName) {
        result = decodeURIComponent(tmp[1]);
      }
    });
  return result;
};

interface LoginFormProps extends WithNavigationProps, WrappedComponentProps {}

@observer
class LoginForm extends React.Component<LoginFormProps> {
  public formRef = React.createRef<FormInstance>();

  public state = {
    errors: [],
  };

  public async componentDidMount() {
    const source = findGetParameter('source');
    const provider = findGetParameter('provider');
    if (source && provider) {
      await this.externalLoginCheck(source, provider);
    }
  }

  public render() {
    if (currentSession.token) {
      return (
        <Space orientation="vertical" size={16}>
          <h1>
            <FormattedMessageWrapper id="Vous êtes connecté !" />
          </h1>
          <Button htmlType="button" onClick={this.logout}>
            <FormattedMessageWrapper id="Déconnexion" />
          </Button>
          <NavLink to="/">
            <FormattedMessageWrapper id="Retour" />
          </NavLink>
        </Space>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <h1>
            <FormattedMessageWrapper id="Connexion" />
          </h1>

          {!!this.state.errors.length && (
            <ul>
              {this.state.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}

          <Form className="login-form" onFinish={this.handleSubmit}>
            <Form.Item
              name="email"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Veuillez indiquer votre identifiant !" />,
                  required: true,
                },
              ]}
            >
              <Input
                autoComplete="email"
                placeholder="Identifiant"
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  message: <FormattedMessageWrapper id="Veuillez taper votre mot de passe !" />,
                  required: true,
                },
              ]}
            >
              <Input.Password
                autoComplete="password"
                // placeholder="Mot de passe"
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                type="password"
              />
            </Form.Item>
            <Form.Item>
              <Button block className="login-form-button" htmlType="submit" type="primary">
                <FormattedMessageWrapper id="Connectez-vous" />
              </Button>
            </Form.Item>
          </Form>
        </div>

        <div className="link-password-recover">
          <Link to="/mot-de-passe-oublie">
            <FormattedMessageWrapper id="Mot de passe oublié" />
          </Link>
        </div>
      </div>
    );
  }

  private endLogin = async () => {
    privateLayoutStore.fetchData();

    await currentFilters.load(currentSession.user.user_id);

    if (currentSession.showId) {
      if (currentSession.isAdminOrSupport) {
        this.props.navigate(`/salons/${currentSession.showId}`);
      } else {
        this.props.navigate(`/salons/${currentSession.showId}/stands`);
      }
    } else {
      this.props.navigate('/'); // push to home and let home make needed redirections
    }
  };

  private externalLoginCheck = async (token: string, sourceReference: string) => {
    await currentSession.logout();

    const externalLoginCheckModel = new ExternalLoginCheckModel();

    const options = {
      data: JSON.stringify({
        sourceReference,
        token,
      }),
      headers: {
        'content-type': 'application/json',
      },
    };

    try {
      const response = await externalLoginCheckModel.save(options);

      await currentSession.onLoggedIn(response.get('token'), response.get('refresh_token'));

      privateLayoutStore.fetchData();

      // don't use endLogin because currentSession use last visited show id that could have been disabled
      const tokenDecoded: any = jwtDecode(token);

      const externalStandIdentifierCollection = new ApiCollection(ExternalStandIdentifierModel);
      await externalStandIdentifierCollection
        .setFilter('tokenIdentifier', tokenDecoded.id || tokenDecoded.user?.id)
        .list();

      const firstExternalStandIdentifier = externalStandIdentifierCollection.first() as ExternalStandIdentifierModel;

      if (firstExternalStandIdentifier?.standId) {
        this.props.navigate(`/stands/${firstExternalStandIdentifier?.standId}`);
      } else {
        this.props.navigate(`/`);
      }
    } catch (err: any) {
      if (err.connectorError.response && err.connectorError.response.status === 401) {
        if (err.connectorError.response.data['hydra:description']) {
          message.error(err.connectorError.response.data['hydra:description']);
        } else {
          messageErrorIntl("Une erreur s'est produite.");
        }
      } else {
        messageErrorIntl("Une erreur s'est produite.");
      }
    }
  };

  private handleSubmit = async (values: any) => {
    try {
      await currentSession.login(values.email.trim(), values.password.trim());
      await this.endLogin();
    } catch {
      messageErrorIntl("La connexion a échoué...");
    }
  };

  private logout = async () => {
    await currentSession.logout();
    window.location.href = '/login';
  };
}

const LoginFormWithInjectedProps = injectIntl(withNavigation(LoginForm));

export default LoginFormWithInjectedProps;
